import { useState, useEffect, useCallback } from 'react'

interface RateLimitConfig {
  maxAttempts: number
  windowMs: number
  storageKey: string
}

interface RateLimitState {
  canSubmit: boolean
  remainingAttempts: number
  resetTime: number | null
  timeUntilReset: number | null
}

export function useRateLimit(
  config: RateLimitConfig,
): [RateLimitState, () => boolean] {
  const { maxAttempts, windowMs, storageKey } = config

  const getStoredAttempts = useCallback((): number[] => {
    try {
      const stored = localStorage.getItem(storageKey)
      if (!stored) return []
      const attempts: number[] = JSON.parse(stored)
      const now = Date.now()
      // Filter out expired attempts
      return attempts.filter((timestamp) => now - timestamp < windowMs)
    } catch {
      return []
    }
  }, [storageKey, windowMs])

  const [state, setState] = useState<RateLimitState>(() => {
    const attempts = getStoredAttempts()
    const remaining = Math.max(0, maxAttempts - attempts.length)
    const oldestAttempt = attempts.length > 0 ? Math.min(...attempts) : null
    const resetTime = oldestAttempt ? oldestAttempt + windowMs : null

    return {
      canSubmit: remaining > 0,
      remainingAttempts: remaining,
      resetTime,
      timeUntilReset: resetTime ? Math.max(0, resetTime - Date.now()) : null,
    }
  })

  // Update state periodically to refresh time until reset
  useEffect(() => {
    const interval = setInterval(() => {
      const attempts = getStoredAttempts()
      const remaining = Math.max(0, maxAttempts - attempts.length)
      const oldestAttempt = attempts.length > 0 ? Math.min(...attempts) : null
      const resetTime = oldestAttempt ? oldestAttempt + windowMs : null

      setState({
        canSubmit: remaining > 0,
        remainingAttempts: remaining,
        resetTime,
        timeUntilReset: resetTime ? Math.max(0, resetTime - Date.now()) : null,
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [getStoredAttempts, maxAttempts, windowMs])

  const recordAttempt = useCallback((): boolean => {
    const attempts = getStoredAttempts()

    if (attempts.length >= maxAttempts) {
      return false
    }

    const newAttempts = [...attempts, Date.now()]
    localStorage.setItem(storageKey, JSON.stringify(newAttempts))

    const remaining = Math.max(0, maxAttempts - newAttempts.length)
    const oldestAttempt = Math.min(...newAttempts)
    const resetTime = oldestAttempt + windowMs

    setState({
      canSubmit: remaining > 0,
      remainingAttempts: remaining,
      resetTime,
      timeUntilReset: Math.max(0, resetTime - Date.now()),
    })

    return true
  }, [getStoredAttempts, maxAttempts, windowMs, storageKey])

  return [state, recordAttempt]
}

// Helper to format time remaining
export function formatTimeRemaining(ms: number): string {
  const minutes = Math.floor(ms / 60000)
  const seconds = Math.floor((ms % 60000) / 1000)

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`
  }
  return `${seconds}s`
}
